import Phaser from 'phaser';
import { ArtefactId } from '../systems/SaveSystem';

/**
 * Подбираемый артефакт. Парит на месте, при подборе — световой всплеск
 * (camera flash + расширяющиеся кольца + растворение в искрах).
 */
export class Artefact extends Phaser.Physics.Arcade.Sprite {
  public readonly id: ArtefactId;
  public collected = false;
  private bobTween?: Phaser.Tweens.Tween;
  private glowTween?: Phaser.Tweens.Tween;
  private glow: Phaser.GameObjects.Image;

  constructor(scene: Phaser.Scene, x: number, y: number, id: ArtefactId) {
    super(scene, x, y, `artefact-${id}`);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.id = id;
    this.setDisplaySize(36, 36);

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setImmovable(true);
    body.setSize(28, 28);

    // Парит — Y-bobbing
    this.bobTween = scene.tweens.add({
      targets: this,
      y: y - 8,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });

    // Мягкое свечение позади (используем focus-aura текстуру)
    this.glow = scene.add.image(x, y, 'focus-aura');
    this.glow.setBlendMode(Phaser.BlendModes.ADD);
    this.glow.setScale(0.35);
    this.glow.setAlpha(0.5);
    this.glow.setDepth((this.depth ?? 0) - 1);
    // В абиссных артефактах подкрашиваем свечение в малиновый
    if (id === 'a3' || id === 'a4') {
      this.glow.setTint(0xff6aa8);
    }
    // Пульсация свечения
    this.glowTween = scene.tweens.add({
      targets: this.glow,
      alpha: 0.25,
      scale: 0.45,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });
  }

  /** Подобрать артефакт. Возвращает true если успешно (ещё не был подобран). */
  pickup(): boolean {
    if (this.collected) return false;
    this.collected = true;

    const scene = this.scene;

    // Camera-flash тёплым/малиновым (зависит от биома артефакта)
    const isAbyss = this.id === 'a3' || this.id === 'a4';
    if (isAbyss) {
      scene.cameras.main.flash(220, 220, 90, 160);
    } else {
      scene.cameras.main.flash(220, 255, 230, 150);
    }

    // Расширяющееся кольцо света
    const ring = scene.add.graphics();
    ring.setDepth(this.depth + 1);
    ring.setPosition(this.x, this.y);
    const ringColor = isAbyss ? 0xff6aa8 : 0xffd070;
    let r = 8;
    let a = 0.9;
    const ringTween = scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 500,
      ease: 'Quad.out',
      onUpdate: (tw) => {
        const t = tw.getValue() ?? 0;
        r = 8 + t * 80;
        a = 0.9 * (1 - t);
        ring.clear();
        ring.lineStyle(4, ringColor, a);
        ring.strokeCircle(0, 0, r);
        ring.lineStyle(2, 0xffffff, a * 0.7);
        ring.strokeCircle(0, 0, r * 0.7);
      },
      onComplete: () => ring.destroy(),
    });
    void ringTween;

    // Растворение самого артефакта — взлёт + scale + fade
    if (this.bobTween) this.bobTween.stop();
    if (this.glowTween) this.glowTween.stop();
    scene.tweens.add({
      targets: [this, this.glow],
      y: this.y - 30,
      alpha: 0,
      scale: 1.4,
      duration: 380,
      ease: 'Quad.out',
      onComplete: () => {
        this.glow.destroy();
        this.destroy();
      },
    });

    return true;
  }

  override destroy(fromScene?: boolean) {
    if (this.bobTween) this.bobTween.stop();
    if (this.glowTween) this.glowTween.stop();
    if (this.glow && this.glow.active) this.glow.destroy();
    super.destroy(fromScene);
  }
}
